<!--
  Super Admin: decorator settlement (OPS-05), with the OPS-07 hold / release
  control.

  Plan: docs/planning/2026-09-02-stripe-payments-payouts-implementation-plan.md
  section "Unit 10 -- Ops and Super Admin surfaces".
  Requirements: OPS-05, OPS-07.

  THE ONE SENTENCE THIS PAGE MUST NEVER SAY IS "PAID".

  ...unless tf-deconetwork actually reported a Transfer. `approved` and
  `transferred` are different states and we never write the second one: it is
  their fact to report, ingested through the Unit 11 payable-results route.
  The Transfer column here is therefore READ-THROUGH -- it keys off the
  presence of their Transfer id, never off our own state.

  Getting this wrong is the LIQ-03 failure in UI form. An approved payable is
  money we have committed and not moved; the gap between approving and
  transferring IS the accepted liquidity exposure (plan section 7.1), and
  visibility is the control the plan chose for it. A settlement view that
  rendered approved as paid would erase exactly the thing it exists to show.
  Hence the summary banner at the top: the outstanding commitment is stated
  as a number before any row is read.

  HOLD NEVER DELETES HISTORY (OPS-07).

  Holding a payable keeps its row, its frozen amounts and its shipment
  anchor, and adds a reason. Releasing restores the state it held before --
  not a guessed one, which would silently de-approve a payable that had
  already been approved. The reason stays after release, because why
  something was once held is history.

  APPROVE, AND WHAT "SENT" MEANS (#619).

  An eligible payable can be approved one at a time or in bulk; both go
  through one confirm dialog that states the total being approved before
  anything is sent. Approval is a decision on our record. Publishing it to
  tf-deconetwork is done by an hourly job behind its own Super Admin switch,
  and the Back office column says whether that happened. "Sent to back
  office" is still NOT paid: the Transfer column keeps reading "Awaiting
  transfer" until they report one. A hold on a payable already sent cannot
  recall it, and the hold dialog says so.

  PAID MANUALLY IS NOT TRANSFERRED (#619, Jake 2026-10-29).

  A Super Admin can record a payment made outside the back office: Mark paid
  takes a payment reference and the date it was sent, and the server stores
  the payer. It is its own state, `paid_manually`, and the Transfer column
  says "Paid manually" with the reference -- never "Transferred", which only
  the back office's report can make true. The server refuses until the
  order's funds have cleared for its tender and says why; the dialog shows
  that reason and stays open. A back-office result that arrives after a
  manual payment is refused by the server and shown on the row: it may mean
  the decorator was paid twice.

  ASCII-only.
-->
<template>
    <div data-testid="sa-payments-settlement">
        <h1 class="sa-page-title">Decorator settlement</h1>

        <p class="sa-ps-intro">
            What decorators are owed, and what tf-deconetwork has actually
            transferred. Transfers are executed by them, not here -- this page
            records what we owe and reflects what they report back.
        </p>

        <!--
          Stated before any row is read. "Approved" is a commitment, not a
          payment, and this is the number that says how much of it is
          outstanding.
        -->
        <div
            v-if="!loading && !errorMsg"
            class="sa-ps-summary"
            data-testid="sa-payables-awaiting-transfer"
        >
            <span class="sa-ps-summary-amount">
                {{ money(summary.approved_awaiting_transfer_cents) }}
            </span>
            <span class="sa-ps-summary-label">
                approved and awaiting transfer
                ({{ summary.approved_awaiting_transfer_count }} payable(s)) --
                money we owe that has not moved
            </span>
        </div>

        <div class="sa-ps-toolbar">
            <button
                v-if="selectedIds.length"
                class="sa-ps-btn sa-ps-btn--primary"
                data-testid="sa-payables-bulk-approve"
                :disabled="busy"
                @click="startApprove(selectedPayables)"
            >
                Approve selected ({{ selectedIds.length }},
                {{ money(selectedTotal) }})
            </button>
            <label class="sa-ps-label" for="sa-ps-state-filter">State</label>
            <select
                id="sa-ps-state-filter"
                v-model="stateFilter"
                class="sa-ps-select"
                data-testid="sa-payments-settlement-state-filter"
                @change="fetch"
            >
                <option value="">All</option>
                <option value="not_eligible">Not eligible</option>
                <option value="eligible">Eligible</option>
                <option value="approved">Approved</option>
                <option value="transferred">Transferred</option>
                <option value="held">Held</option>
                <option value="exception">Exception</option>
                <option value="paid_manually">Paid manually</option>
            </select>
        </div>

        <p
            v-if="approveMessage"
            class="sa-ps-state sa-ps-state--error"
            data-testid="sa-payables-approve-message"
        >
            {{ approveMessage }}
        </p>

        <p
            v-if="loading"
            class="sa-ps-state"
            data-testid="sa-payments-settlement-loading"
        >
            Loading...
        </p>
        <p
            v-else-if="errorMsg"
            class="sa-ps-state sa-ps-state--error"
            data-testid="sa-payments-settlement-error"
        >
            {{ errorMsg }}
        </p>
        <p
            v-else-if="payables.length === 0"
            class="sa-ps-state"
            data-testid="sa-payments-settlement-empty"
        >
            No decorator payables yet. One is created for an order when the
            decorator reports it fully shipped.
        </p>

        <table
            v-else
            class="sa-table"
            data-testid="sa-payments-settlement-table"
        >
            <thead>
                <tr>
                    <th><span class="sa-ps-sr">Select</span></th>
                    <th>Order</th>
                    <th>Decorator</th>
                    <th>Decoration</th>
                    <th>Handling</th>
                    <th>Total owed</th>
                    <th>State</th>
                    <th>Back office<br /><small>sent for settlement</small></th>
                    <th>Transfer<br /><small>reported by tf-deconetwork</small></th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="p in payables"
                    :key="p.id"
                    :data-testid="`sa-payable-row-${p.id}`"
                >
                    <td>
                        <input
                            v-if="p.state === 'eligible'"
                            v-model="selectedIds"
                            type="checkbox"
                            :value="p.id"
                            :aria-label="`Select ${p.order_key}`"
                            :data-testid="`sa-payable-select-${p.id}`"
                        />
                    </td>
                    <td>
                        <router-link
                            class="sa-ps-link"
                            :data-testid="`sa-payable-order-${p.id}`"
                            :to="`/admin/payments/reconciliation/${p.order_id}`"
                        >
                            {{ p.order_key }}
                        </router-link>
                    </td>
                    <td>{{ p.decorator_tenant_id || "--" }}</td>
                    <td>{{ money(p.decoration_payout_cents) }}</td>
                    <td>
                        {{ money(p.handling_allocation_cents) }}
                        <span class="sa-ps-hint">
                            ({{ p.shipment_count }})
                        </span>
                    </td>
                    <td>{{ money(p.total_cents) }}</td>
                    <td :data-testid="`sa-payable-state-${p.id}`">
                        {{ p.state }}
                        <div
                            v-if="p.hold_reason && p.state === 'held'"
                            class="sa-ps-reason"
                            :data-testid="`sa-payable-hold-reason-${p.id}`"
                        >
                            {{ p.hold_reason }}
                        </div>
                    </td>

                    <!--
                      What WE sent, never what was paid. Driven by the
                      server's publish_status.
                    -->
                    <td :data-testid="`sa-payable-publish-${p.id}`">
                        <span
                            v-if="p.publish_status === 'published'"
                            class="sa-ps-badge sa-ps-badge--pending"
                        >Sent to back office</span>
                        <template v-else-if="p.publish_status === 'failing'">
                            <span class="sa-ps-badge sa-ps-badge--bad">
                                Publish failing
                            </span>
                            <div class="sa-ps-hint">
                                {{ p.publish_attempts }} attempt(s):
                                {{ p.last_publish_error }}
                            </div>
                        </template>
                        <span v-else class="sa-ps-hint">Not sent</span>
                    </td>

                    <!--
                      THE COLUMN THAT MUST NOT LIE. `is_transferred` comes
                      from the server, which derives it from the presence of
                      tf-deconetwork's Transfer id -- never from our own
                      state. An approved payable reads "Awaiting transfer".
                    -->
                    <td :data-testid="`sa-payable-transfer-${p.id}`">
                        <template v-if="p.state === 'paid_manually'">
                            <span class="sa-ps-badge sa-ps-badge--good">
                                Paid manually
                            </span>
                            <div class="sa-ps-hint">
                                {{ p.manual_payment_reference }},
                                {{ p.manual_payment_date }}
                            </div>
                            <div
                                v-if="p.refused_result_at"
                                class="sa-ps-reason"
                                :data-testid="`sa-payable-refused-result-${p.id}`"
                            >
                                The back office reported a result after this
                                manual payment ({{ p.refused_result_at }}). It
                                was not applied -- check the decorator was not
                                paid twice: {{ p.refused_result_detail }}
                            </div>
                        </template>
                        <template v-else-if="p.is_transferred">
                            <span class="sa-ps-badge sa-ps-badge--good">
                                Transferred
                            </span>
                            <div class="sa-ps-hint">
                                {{ p.stripe_transfer_id }}
                            </div>
                        </template>
                        <span
                            v-else-if="p.transfer_failure_reason"
                            class="sa-ps-badge sa-ps-badge--bad"
                        >Failed</span>
                        <span
                            v-else
                            class="sa-ps-badge sa-ps-badge--pending"
                        >Awaiting transfer</span>
                    </td>

                    <td>
                        <button
                            v-if="p.state === 'eligible'"
                            class="sa-ps-btn sa-ps-btn--primary"
                            :data-testid="`sa-payable-approve-${p.id}`"
                            :disabled="busy"
                            @click="startApprove([p])"
                        >
                            Approve
                        </button>
                        <button
                            v-if="canMarkPaid(p)"
                            class="sa-ps-btn"
                            :data-testid="`sa-payable-mark-paid-${p.id}`"
                            :disabled="busy"
                            @click="startMarkPaid(p)"
                        >
                            Mark paid
                        </button>
                        <button
                            v-if="canHold(p)"
                            class="sa-ps-btn"
                            :data-testid="`sa-payable-hold-${p.id}`"
                            :disabled="busy"
                            @click="startHold(p)"
                        >
                            Hold
                        </button>
                        <button
                            v-if="p.state === 'held'"
                            class="sa-ps-btn sa-ps-btn--primary"
                            :data-testid="`sa-payable-release-${p.id}`"
                            :disabled="busy"
                            @click="release(p)"
                        >
                            Release
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <div
            v-if="pending"
            class="sa-ps-dialog"
            data-testid="sa-payable-hold-dialog"
        >
            <h2 class="sa-ps-dialog-title">
                Hold payable for {{ pending.order_key }}
            </h2>
            <p class="sa-ps-dialog-note">
                A hold stops this payable being published for settlement. It
                does not delete anything -- the amounts and the shipment
                record stay, and releasing restores the state it is in now.
            </p>
            <p
                v-if="pending.published_at"
                class="sa-ps-dialog-note sa-ps-reason"
                data-testid="sa-payable-hold-published-warning"
            >
                This payable was already sent to the back office. A hold
                here marks our record only; it does not recall the
                instruction. Ask the back office to stop it.
            </p>
            <label class="sa-ps-label" for="sa-ps-reason">Reason</label>
            <input
                id="sa-ps-reason"
                v-model="reason"
                class="sa-ps-input"
                data-testid="sa-payable-hold-reason-input"
                placeholder="e.g. decorator dispute -- awaiting photos"
            />
            <div class="sa-ps-dialog-actions">
                <button
                    class="sa-ps-btn"
                    data-testid="sa-payable-hold-cancel"
                    @click="cancel"
                >
                    Cancel
                </button>
                <button
                    class="sa-ps-btn sa-ps-btn--primary"
                    data-testid="sa-payable-hold-confirm"
                    :disabled="busy || !reason.trim()"
                    @click="confirmHold"
                >
                    Hold
                </button>
            </div>
        </div>

        <div
            v-if="markingPaid"
            class="sa-ps-dialog"
            data-testid="sa-payable-mark-paid-dialog"
        >
            <h2 class="sa-ps-dialog-title">
                Mark payable for {{ markingPaid.order_key }} paid
            </h2>
            <p class="sa-ps-dialog-note">
                Record a payment made to the decorator outside the back
                office. It is recorded under your name. ThreadFlow checks
                that this order's funds have cleared first.
            </p>
            <p
                class="sa-ps-summary-amount"
                data-testid="sa-payable-mark-paid-total"
            >
                {{ money(markingPaid.total_cents) }}
            </p>
            <p
                v-if="markingPaid.published_at"
                class="sa-ps-dialog-note sa-ps-reason"
                data-testid="sa-payable-mark-paid-published-warning"
            >
                This payable was already sent to the back office. Ask them
                not to pay it: a transfer they report afterwards will not be
                applied and will be flagged here as a possible double
                payment.
            </p>
            <label class="sa-ps-label" for="sa-ps-pay-ref">
                Payment reference
            </label>
            <input
                id="sa-ps-pay-ref"
                v-model="payReference"
                class="sa-ps-input"
                data-testid="sa-payable-mark-paid-reference"
                maxlength="255"
                placeholder="e.g. check 10442 or wire reference"
            />
            <label class="sa-ps-label" for="sa-ps-pay-date">
                Date paid
            </label>
            <input
                id="sa-ps-pay-date"
                v-model="payDate"
                type="date"
                class="sa-ps-input"
                data-testid="sa-payable-mark-paid-date"
            />
            <p
                v-if="markPaidError"
                class="sa-ps-state sa-ps-state--error"
                data-testid="sa-payable-mark-paid-error"
            >
                {{ markPaidError }}
            </p>
            <div class="sa-ps-dialog-actions">
                <button
                    class="sa-ps-btn"
                    data-testid="sa-payable-mark-paid-cancel"
                    @click="cancelMarkPaid"
                >
                    Cancel
                </button>
                <button
                    class="sa-ps-btn sa-ps-btn--primary"
                    data-testid="sa-payable-mark-paid-confirm"
                    :disabled="busy || !payReference.trim() || !payDate"
                    @click="confirmMarkPaid"
                >
                    Mark paid
                </button>
            </div>
        </div>

        <div
            v-if="approving"
            class="sa-ps-dialog"
            data-testid="sa-payables-approve-dialog"
        >
            <h2 class="sa-ps-dialog-title">
                Approve {{ approving.length }} payable(s)
            </h2>
            <p class="sa-ps-dialog-note">
                Approving authorizes paying these decorators. Approved
                payables are sent to the back office for settlement when
                sending is switched on. Nothing is paid until the back office
                reports a transfer.
            </p>
            <p
                class="sa-ps-summary-amount"
                data-testid="sa-payables-approve-total"
            >
                {{ money(approvingTotal) }} total
            </p>
            <div class="sa-ps-dialog-actions">
                <button
                    class="sa-ps-btn"
                    data-testid="sa-payables-approve-cancel"
                    @click="cancelApprove"
                >
                    Cancel
                </button>
                <button
                    class="sa-ps-btn sa-ps-btn--primary"
                    data-testid="sa-payables-approve-confirm"
                    :disabled="busy"
                    @click="confirmApprove"
                >
                    Approve
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/services/api";

function money(cents) {
    if (cents === null || cents === undefined) return "--";
    return `$${(Number(cents) / 100).toFixed(2)}`;
}

// Mirrors the server's HOLDABLE_STATES. `transferred` is absent because the
// money has already moved -- offering a Hold button there would promise a
// control we do not have (reversing a Transfer is tf-deconetwork's
// operation). The server refuses it with a 409 regardless; hiding the button
// is the affordance, not the control.
const HOLDABLE = ["not_eligible", "eligible", "approved"];

// Mirrors the server's MANUALLY_PAYABLE_STATES. The server also refuses
// until the order's funds have cleared; that answer comes back as a reason.
const MANUALLY_PAYABLE = ["eligible", "approved", "exception"];

function todayIso() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export default {
    name: "SuperAdminPaymentsSettlement",
    data() {
        return {
            loading: true,
            busy: false,
            errorMsg: "",
            payables: [],
            summary: {
                approved_awaiting_transfer_cents: 0,
                approved_awaiting_transfer_count: 0,
            },
            stateFilter: "",
            pending: null,
            reason: "",
            selectedIds: [],
            approving: null,
            approveMessage: "",
            markingPaid: null,
            payReference: "",
            payDate: "",
            markPaidError: "",
        };
    },
    computed: {
        selectedPayables() {
            return this.payables.filter((p) =>
                this.selectedIds.includes(p.id)
            );
        },
        selectedTotal() {
            return this.selectedPayables.reduce(
                (sum, p) => sum + Number(p.total_cents || 0),
                0
            );
        },
        approvingTotal() {
            return (this.approving || []).reduce(
                (sum, p) => sum + Number(p.total_cents || 0),
                0
            );
        },
    },
    async mounted() {
        await this.fetch();
    },
    methods: {
        money,
        canHold(payable) {
            return HOLDABLE.includes(payable.state);
        },
        canMarkPaid(payable) {
            return MANUALLY_PAYABLE.includes(payable.state);
        },
        startMarkPaid(payable) {
            this.markingPaid = payable;
            this.payReference = "";
            this.payDate = todayIso();
            this.markPaidError = "";
        },
        cancelMarkPaid() {
            this.markingPaid = null;
            this.markPaidError = "";
        },
        async confirmMarkPaid() {
            const payable = this.markingPaid;
            const reference = this.payReference.trim();
            if (!payable || !reference || !this.payDate) return;
            this.busy = true;
            this.markPaidError = "";
            try {
                await api.post(
                    `/superadmin/payments/payables/${payable.id}/mark-paid`,
                    {
                        reference,
                        payment_date: this.payDate,
                        // The dialog showed the "already sent to the back
                        // office" warning; the server requires this when
                        // the payable was published, and audits it.
                        acknowledge_published: Boolean(payable.published_at),
                    }
                );
                this.markingPaid = null;
                await this.fetch();
            } catch (e) {
                // Keep the dialog open with the server's reason: "funds not
                // cleared" is an answer the operator has to read, not a
                // generic failure.
                const data = e && e.response && e.response.data;
                this.markPaidError =
                    (data && (data.reason || data.error)) ||
                    `Could not mark payable #${payable.id} paid.`;
            } finally {
                this.busy = false;
            }
        },
        async fetch() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const query = this.stateFilter
                    ? `?state=${encodeURIComponent(this.stateFilter)}`
                    : "";
                const resp = await api.get(
                    `/superadmin/payments/payables${query}`
                );
                const data = resp && resp.data ? resp.data : resp;
                this.payables = (data && data.payables) || [];
                // Drop selections for rows that are no longer eligible.
                const eligible = new Set(
                    this.payables
                        .filter((p) => p.state === "eligible")
                        .map((p) => p.id)
                );
                this.selectedIds = this.selectedIds.filter((id) =>
                    eligible.has(id)
                );
                if (data && data.summary) {
                    this.summary = data.summary;
                }
            } catch (e) {
                this.errorMsg =
                    "Could not load decorator payables. Please try again.";
            } finally {
                this.loading = false;
            }
        },
        startApprove(payables) {
            this.approving = payables.slice();
            this.approveMessage = "";
        },
        cancelApprove() {
            this.approving = null;
        },
        async confirmApprove() {
            const payables = this.approving || [];
            if (!payables.length) return;
            this.busy = true;
            try {
                // One endpoint for one or many: the server decides each id
                // on its own and says what happened to it.
                const resp = await api.post(
                    "/superadmin/payments/payables/approve",
                    { ids: payables.map((p) => p.id) }
                );
                const data = resp && resp.data ? resp.data : resp;
                const results = (data && data.results) || [];
                const notApproved = results.filter(
                    (r) =>
                        r.status !== "approved" &&
                        r.status !== "already_approved"
                );
                this.approveMessage = notApproved.length
                    ? "Not approved: " +
                      notApproved
                          .map(
                              (r) =>
                                  `#${r.id} (${r.state || r.status})`
                          )
                          .join(", ")
                    : "";
                this.approving = null;
                this.selectedIds = [];
                await this.fetch();
            } catch (e) {
                this.approving = null;
                this.approveMessage = "Could not approve. Please try again.";
            } finally {
                this.busy = false;
            }
        },
        startHold(payable) {
            this.pending = payable;
            this.reason = "";
        },
        cancel() {
            this.pending = null;
            this.reason = "";
        },
        async confirmHold() {
            const payable = this.pending;
            if (!payable || !this.reason.trim()) return;
            this.busy = true;
            try {
                await api.post(
                    `/superadmin/payments/payables/${payable.id}/hold`,
                    { reason: this.reason.trim() }
                );
                this.pending = null;
                this.reason = "";
                await this.fetch();
            } catch (e) {
                this.errorMsg = `Could not hold payable #${payable.id}.`;
            } finally {
                this.busy = false;
            }
        },
        async release(payable) {
            this.busy = true;
            try {
                await api.post(
                    `/superadmin/payments/payables/${payable.id}/release`
                );
                // Re-read rather than guessing the restored state locally --
                // the server restores state_before_hold and the client must
                // not second-guess which state that was.
                await this.fetch();
            } catch (e) {
                this.errorMsg = `Could not release payable #${payable.id}.`;
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #1a202c;
}
.sa-ps-intro {
    font-size: 14px;
    color: #4a5568;
    margin: 0 0 16px;
    max-width: 760px;
}
.sa-ps-summary {
    border: 1px solid #f6e05e;
    background: #fffff0;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
    max-width: 760px;
}
.sa-ps-summary-amount {
    font-size: 20px;
    font-weight: 700;
    color: #b7791f;
    margin-right: 8px;
}
.sa-ps-summary-label {
    font-size: 13px;
    color: #4a5568;
}
.sa-ps-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}
.sa-ps-label {
    font-size: 13px;
    color: #4a5568;
}
.sa-ps-select,
.sa-ps-input {
    padding: 5px 8px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    font-size: 13px;
}
.sa-ps-input {
    width: 100%;
    box-sizing: border-box;
    font-size: 14px;
}
.sa-ps-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-ps-state--error {
    color: #c53030;
}
.sa-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.sa-table th,
.sa-table td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: top;
}
.sa-table th small {
    font-weight: 400;
    color: var(--tf-fg-quiet);
}
.sa-ps-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
}
.sa-ps-badge--good {
    background: #f0fff4;
    color: #2f855a;
}
.sa-ps-badge--pending {
    background: #fffaf0;
    color: #b7791f;
}
.sa-ps-badge--bad {
    background: #fff5f5;
    color: #c53030;
}
.sa-ps-hint {
    font-size: 12px;
    color: var(--tf-fg-quiet);
}
.sa-ps-reason {
    font-size: 12px;
    color: #c05621;
    margin-top: 2px;
}
.sa-ps-link {
    color: #2b6cb0;
    text-decoration: none;
}
.sa-ps-btn {
    font-size: 13px;
    padding: 4px 10px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
}
.sa-ps-btn:disabled {
    opacity: 0.6;
    cursor: default;
}
.sa-ps-btn--primary {
    border-color: #2b6cb0;
    color: #2b6cb0;
}
.sa-ps-dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    padding: 20px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    width: 440px;
    z-index: 100;
}
.sa-ps-dialog-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
}
.sa-ps-dialog-note {
    font-size: 13px;
    color: #4a5568;
    margin: 0 0 12px;
}
.sa-ps-sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
}
.sa-ps-dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
}
</style>
